import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, animate } from "framer-motion";
import { formatTime } from "./gameUtils";
import { StarIcon, SparkleIcon } from "../ui-icons";

const F = { fontFamily: "'Fredoka', sans-serif" };
const GREEN = "#189970";

const levelNote = (change) => {
  if (change === 1) return "Leveling up — more pieces next!";
  if (change === -1) return "Next one's a little easier";
  return null;
};

/** Small stat pill under the hero. */
const StatPill = ({ badge, children }) => (
  <div
    className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5"
    style={{ boxShadow: "0 2px 8px rgba(11,26,51,0.08)", border: "1.5px solid rgba(24,153,112,0.14)" }}
  >
    <span className="text-sm" style={{ color: GREEN, fontWeight: 700 }} aria-hidden>
      {badge}
    </span>
    <span className="text-[13px]" style={{ ...F, color: "#222B54", fontWeight: 650 }}>
      {children}
    </span>
  </div>
);

/** Score counts up from 0 while the modal opens. */
const CountUpScore = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(0, value, {
      duration: 0.9,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value]);
  return <>{display}</>;
};

const SPARKLES = [
  { left: "8%", top: "14%", size: 16, delay: 0.35 },
  { left: "88%", top: "10%", size: 13, delay: 0.5 },
  { left: "6%", top: "58%", size: 12, delay: 0.65 },
  { left: "90%", top: "52%", size: 15, delay: 0.8 },
];

const PuzzleVictoryModal = ({
  open,
  score,
  starsAwarded,
  isNewBest,
  seconds,
  moves,
  levelChange,
  boardImage,
  onPlayAgain,
  onDone,
}) => {
  const note = levelNote(levelChange);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center px-8"
          style={{ background: "rgba(8,20,40,0.55)", backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-[370px] overflow-hidden rounded-[32px]"
            style={{
              background: "linear-gradient(180deg, #FFFDF6 0%, #F3FAF3 100%)",
              boxShadow: "0 28px 70px rgba(0,0,0,0.35), 0 0 0 6px rgba(255,255,255,0.35)",
            }}
            initial={{ scale: 0.8, opacity: 0, y: 22 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 21 }}
          >
            {/* Rotating celebration rays behind the hero */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[250px] overflow-hidden" aria-hidden>
              <div
                className="pvm-rays absolute left-1/2 top-[110px] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2"
                style={{
                  background:
                    "repeating-conic-gradient(rgba(255,199,41,0.16) 0deg 14deg, rgba(255,199,41,0) 14deg 28deg)",
                  maskImage: "radial-gradient(circle, black 0%, transparent 62%)",
                  WebkitMaskImage: "radial-gradient(circle, black 0%, transparent 62%)",
                }}
              />
            </div>

            {SPARKLES.map((s, i) => (
              <motion.span
                key={i}
                className="pointer-events-none absolute"
                style={{ left: s.left, top: s.top }}
                aria-hidden
                initial={{ scale: 0, rotate: -30, opacity: 0 }}
                animate={{ scale: [0, 1.25, 1], rotate: 0, opacity: 1 }}
                transition={{ delay: s.delay, duration: 0.5 }}
              >
                <SparkleIcon size={s.size} />
              </motion.span>
            ))}

            <div className="relative px-6 pt-7">
              {/* The kid's completed picture IS the celebration */}
              <motion.div
                className="relative mx-auto w-[210px]"
                initial={{ scale: 0.4, rotate: -10, opacity: 0 }}
                animate={{ scale: 1, rotate: -2.5, opacity: 1 }}
                transition={{ type: "spring", stiffness: 210, damping: 15, delay: 0.08 }}
              >
                <div
                  className="pvm-shine relative overflow-hidden rounded-[18px]"
                  style={{
                    border: "6px solid #FFFFFF",
                    boxShadow: "0 14px 30px rgba(11,26,51,0.28)",
                  }}
                >
                  {boardImage ? (
                    <img
                      src={boardImage}
                      alt="Your finished puzzle"
                      className="block aspect-square w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="aspect-square w-full" style={{ background: "linear-gradient(135deg, #38BD87, #189970)" }} />
                  )}
                </div>

                {isNewBest && (
                  <motion.span
                    className="absolute -right-6 -top-3 rounded-full px-3 py-1 text-[11px] uppercase tracking-wide text-white"
                    style={{ ...F, fontWeight: 700, background: "#ED4C5C", rotate: "8deg", boxShadow: "0 4px 10px rgba(237,76,92,0.4)" }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 12, delay: 0.45 }}
                  >
                    New best!
                  </motion.span>
                )}

                <motion.span
                  className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-5 py-1.5 text-[15px] text-white"
                  style={{
                    ...F,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #FFC729 0%, #F2A400 100%)",
                    boxShadow: "0 6px 14px rgba(242,164,0,0.4)",
                  }}
                  initial={{ scale: 0, y: 8 }}
                  animate={{ scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 280, damping: 14, delay: 0.28 }}
                >
                  Puzzle complete!
                </motion.span>
              </motion.div>

              <p className="mt-7 text-center text-[30px] leading-none" style={{ ...F, color: "#222B54", fontWeight: 700 }}>
                <CountUpScore value={score} />
                <span className="ml-1.5 text-[16px]" style={{ color: "#66788F", fontWeight: 600 }}>
                  points
                </span>
              </p>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <StatPill badge="◷">{formatTime(seconds)}</StatPill>
                <StatPill badge="✓">{moves} moves</StatPill>
                {note && <StatPill badge="✦">{note}</StatPill>}
              </div>

              {starsAwarded > 0 && (
                <motion.div
                  className="mx-auto mt-3 flex w-fit items-center gap-1.5 rounded-full px-4 py-2"
                  style={{ ...F, color: "#B07A00", fontWeight: 700, background: "rgba(255,199,41,0.2)" }}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [0.5, 1.15, 1], opacity: 1 }}
                  transition={{ delay: 0.55, duration: 0.45 }}
                >
                  <StarIcon size={17} />
                  +{starsAwarded} {starsAwarded === 1 ? "star" : "stars"} earned
                </motion.div>
              )}
            </div>

            <div className="px-6 pb-6 pt-4">
              <button
                type="button"
                onClick={onPlayAgain}
                className="h-[54px] w-full rounded-full text-[17px] text-white transition-transform hover:scale-[1.02] active:scale-[0.97]"
                style={{
                  ...F,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #38BD87 0%, #189970 100%)",
                  boxShadow: "0 8px 18px rgba(24,153,112,0.38), inset 0 2px 0 rgba(255,255,255,0.25)",
                }}
              >
                ↻&nbsp;&nbsp;Play Again
              </button>
              <button
                type="button"
                onClick={onDone}
                className="mt-1.5 h-11 w-full text-base"
                style={{ ...F, color: "#66788F", fontWeight: 600 }}
              >
                Done
              </button>
            </div>

            <style>{`
              .pvm-rays { animation: pvmSpin 26s linear infinite; }
              @keyframes pvmSpin { to { transform: translate(-50%, -50%) rotate(360deg); } }
              .pvm-shine::after {
                content: "";
                position: absolute;
                inset: -40%;
                background: linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.55) 50%, transparent 58%);
                transform: translateX(-120%);
                animation: pvmShine 1.1s ease-out 0.5s 1 forwards;
                pointer-events: none;
              }
              @keyframes pvmShine { to { transform: translateX(120%); } }
              @media (prefers-reduced-motion: reduce) {
                .pvm-rays { animation: none; }
                .pvm-shine::after { animation: none; }
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PuzzleVictoryModal;
